import React, { ChangeEvent, FC, useState } from "react";
import { MediumHeader, SemibigHeader } from "@narvar/nth-bit-header";
import Paragraph from "@narvar/nth-bit-paragraph";
import Divider from "@narvar/nth-bit-divider";
import TextInput, { SupportedInputType } from "@narvar/nth-block-text-input";
import { useSettings, withSettingsProvider } from "@narvar/nth-hook-settings";
import { useTheme, withThemeProvider } from "@narvar/nth-hook-theme";
import { withTranslationsProvider } from "@narvar/nth-hook-translations";
import { Items } from "./Items";
import { Total } from "./Total";
import {
  ErrorBanner,
  Language,
  LocationCard,
  Spinner,
  StickyFooter,
  Footer,
  ItemCardSkeleton,
  LineItemSkeleton,
  Backdrop,
} from "./components";
import { ReviewSummaryRawProps } from "./types";
import Checkbox from "@narvar/nth-block-checkbox";
import { EMAIL_ADDRESS_PATTERN, validatePattern } from "./utils";
import ReshopOptIn from "./ReshopOptIn/ReshopOptIn";
import { ReshopOptions } from "./ReshopOptIn/types";
import { ShopNowItems } from "./ShopNowItems";
import { useStyles } from "./ReviewSummaryRawStyles";
import {
  FooterButtonType,
  ReturnPayload,
  StickyFooterOnShowEvent,
  useReturnsConfig,
  useLanguage,
} from "@narvar/nth-kit-returns-headless";
// import ClaimAttestation from "./components/ClaimAttestation/ClaimAttestation";

const HIPAA_CSR_ROLES = ["csrHipaa", "csrAdminHipaa"];

export const checkIfEmailFieldIsValid = (
  email = "",
  options?: {
    canAddCustomerEmail?: boolean;
    canUpdateCustomerEmailHipaa?: boolean;
    hasAssociatedEmail?: boolean;
  },
) => {
  const {
    canAddCustomerEmail,
    canUpdateCustomerEmailHipaa,
    hasAssociatedEmail,
  } = options || {};
  const isValidEmail = validatePattern(email, EMAIL_ADDRESS_PATTERN);

  if (!canAddCustomerEmail && !canUpdateCustomerEmailHipaa) {
    // The check for email state validity is true if the user can't update the email in any way, therefore there is no email field to validate.
    return true;
  }

  if (canAddCustomerEmail && isValidEmail && !canUpdateCustomerEmailHipaa) {
    // The shopify user canAddCustomerEmail, and the entered email is valid
    return true;
  }

  if (
    canUpdateCustomerEmailHipaa &&
    !hasAssociatedEmail &&
    isValidEmail &&
    !canAddCustomerEmail
  ) {
    // The HIPAA user canUpdateCustomerEmailHipaa, the order DOES NOT have an email to begin with, and the entered email is valid.
    return true;
  }

  if (
    canUpdateCustomerEmailHipaa &&
    hasAssociatedEmail &&
    (isValidEmail || email === "")
  ) {
    // The HIPAA user canUpdateCustomerEmailHipaa, the order has an email to begin with, and the newly entered email is valid OR the email field is empty.
    return true;
  }

  return false;
};

const ReviewSummaryContent: FC<ReviewSummaryRawProps> = ({
  canAddCustomerEmail,
  canUpdateCustomerEmailHipaa,
  locale,
  includeCurrency,
  orderItems,
  selectedItems,
  shopNowItems,
  dropOffLocation,
  fees,
  refund,
  error,
  hasAssociatedEmail,
  loading,
  mailTo,
  renderAddOn,
  returnCreditsSummary,
  onEmailChange,
  policyChecked,
  policyLabel,
  onPolicyChange,
  type,
  preferencesStatus,
  savePreferences,
  onSavePreferencesChange,
  reshopOptions,
  onReshopOptionsChange,
}) => {
  const { itemDisplay } = useSettings();
  const { bodytextDefaultNone } = useTheme();

  const styles = useStyles();

  const returnsReviewAddEmailInputLabel = useLanguage({
    langKey: "returnsReviewAddEmailInputLabel",
    asString: true,
  }).value;

  if (loading) {
    return (
      <div>
        <ItemCardSkeleton />
        <LineItemSkeleton />
        <Divider />
        <LineItemSkeleton />
        <Divider />
        <LineItemSkeleton />
        <Divider />
        <LineItemSkeleton />
        <ItemCardSkeleton />
      </div>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasExchange = selectedItems.some((item: any) => item.exchangeTo);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasReturn = selectedItems.some((item: any) => !item.exchangeTo);

  const handlePolicyChange = (event: ChangeEvent<HTMLInputElement>) => {
    onPolicyChange?.(Boolean(event.target.checked));
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onEmailChange?.(event.target.value);
  };
  const handleSavePreferencesChange = (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    onSavePreferencesChange?.(Boolean(event.target.checked));
  };

  return (
    <div style={bodytextDefaultNone}>
      <header className={styles.headerContainer}>
        {error && (
          <div style={{ marginBottom: "1rem" }}>
            <ErrorBanner>
              {React.isValidElement(error) ? (
                error
              ) : (
                <Language langKey="returnsReviewError" />
              )}
            </ErrorBanner>
          </div>
        )}
        <SemibigHeader>
          <Language langKey="returnsReviewHeader" />
        </SemibigHeader>
        <Paragraph>
          <Language langKey={`returnsReviewDescription${type ?? ""}`} />
        </Paragraph>
      </header>
      <main className={styles.main}>
        <MediumHeader>
          <Language
            langKey={
              hasExchange && hasReturn
                ? "returnsReviewSubHeaderReturnAndExchange"
                : hasExchange
                  ? "returnsReviewSubHeaderExchange"
                  : "returnsReviewSubHeader"
            }
          />
        </MediumHeader>
        <Divider />
        <Items
          {...{ orderItems, selectedItems, locale, includeCurrency }}
          itemDisplay={itemDisplay}
        />
        {shopNowItems?.length && (
          <>
            <MediumHeader>
              <Language langKey="returnReviewTitleShopNow" />
            </MediumHeader>
            <Divider />
            <ShopNowItems
              {...{ shopNowItems, locale, includeCurrency }}
              itemDisplay={itemDisplay}
            />
          </>
        )}

        <Divider />
        <Total
          items={selectedItems}
          fees={fees}
          returnCreditsSummary={returnCreditsSummary}
          refund={refund}
          locale={locale}
          includeCurrency={includeCurrency}
        />
      </main>
      {dropOffLocation && (
        <div>
          <MediumHeader>
            <Language langKey="returnsReviewDropOffLocationHeader" />
          </MediumHeader>
          <Divider />
          <LocationCard location={dropOffLocation} active transparent />
        </div>
      )}
      <div className={styles.conditionalSections}>
        {canUpdateCustomerEmailHipaa && (
          <div className={styles.addEmailToReturn}>
            <MediumHeader>
              <Language langKey="returnsReviewAddEmailHeader" />
            </MediumHeader>
            <p className={styles.paragraph}>
              <Language
                langKey={
                  hasAssociatedEmail
                    ? "returnsReviewUpdateEmailDescription"
                    : "returnsReviewAddEmailDescription"
                }
              />
            </p>
            <TextInput
              id="customerEmail"
              type={SupportedInputType.email}
              label={returnsReviewAddEmailInputLabel}
              animateLabel
              value={mailTo}
              onChange={handleInputChange}
              containerOverridesStyle={{ background: "#fff" }}
            />
          </div>
        )}
        {React.isValidElement(renderAddOn)
          ? renderAddOn
          : typeof renderAddOn === "function"
            ? renderAddOn()
            : null}
        {policyLabel && (
          <Checkbox
            checked={policyChecked}
            id={"policyCheckbox"}
            onChange={handlePolicyChange}>
            {policyLabel}
          </Checkbox>
        )}
        {/* TODO: confirm with design, do we need to show the claim attestation? */}
        {/* {hasClaim && (
          <ClaimAttestation
            isCertified={isCertified}
            onChangeCertified={onChangeCertified}
            signature={signature}
            onChangeSignature={onChangeSignature}
            shopName={shopName}
          />
        )} */}
        {preferencesStatus && !preferencesStatus.profileStatus && (
          <div className={styles.savePreferences}>
            <Checkbox
              id="savePreferencesCheckbox"
              checked={savePreferences}
              onChange={handleSavePreferencesChange}>
              <Language langKey="returnsReviewPreferencesCheckboxLabel" />
            </Checkbox>
            {canAddCustomerEmail && (
              <>
                <Language langKey="returnsReviewPreferencesInputLabel" />
                <TextInput
                  className={styles.preferencesEmail}
                  id="customerEmail"
                  type={SupportedInputType.email}
                  value={mailTo}
                  onChange={handleInputChange}
                />
              </>
            )}
          </div>
        )}
        {reshopOptions && (
          <ReshopOptIn
            value={reshopOptions}
            error={reshopOptions.error}
            onChange={onReshopOptionsChange}
          />
        )}
      </div>
    </div>
  );
};

const ReviewSummaryRawC = ({
  locale,
  includeCurrency,
  orderItems,
  selectedItems,
  shopNowItems,
  hasClaim,
  shopName,
  isCertified,
  onChangeCertified,
  signature,
  onChangeSignature,
  fees,
  refund,
  dropOffLocation,
  hasAssociatedEmail,
  error,
  loading,
  disabled,
  allowRetry,
  submitting,
  returnCart,
  mailTo,
  onBack,
  onSubmit,
  renderAddOn,
  returnCreditsSummary,
  policyChecked,
  policyLabel,
  preferencesStatus,
  savePreferences,
  onChange,
  progressiveStep,
  activeStep,
  userRole,
  reshopOptions,
}: ReviewSummaryRawProps) => {
  const { isHipaaCompliant } = useReturnsConfig();
  const { bodytextDefaultNone } = useTheme();

  const styles = useStyles();

  // Shopify customers without an associated email and who can save preferences.
  const canAddCustomerEmail =
    !isHipaaCompliant && !hasAssociatedEmail && savePreferences;

  // Using isHipaaCompliant below is repetitive, but is an extra safety check.
  // Hipaa CSRs can update email
  const canUpdateCustomerEmailHipaa =
    isHipaaCompliant && HIPAA_CSR_ROLES.includes(userRole ?? "");

  const backLabel = useLanguage({
    langKey: "returnsActionsBack",
    asString: true,
  }).value;
  const submitLabel = useLanguage({
    langKey: "returnsActionsSubmit",
    asString: true,
  }).value;
  const [errorPosition, setErrorPosition] = useState<"inline" | "footer">(
    "inline",
  );

  const handleFooterEvent = (event: ReturnPayload<FooterButtonType>) => {
    if (event.value === "back") {
      onBack?.({
        value: {},
        area: "returnReview",
        event: "back",
      });
    }
    if (event.value === "submit") {
      onSubmit?.({
        value: {},
        area: "returnReview",
        event: "submit",
      });
    }
  };

  const handlePolicyChange = (event: boolean) => {
    onChange?.({
      area: "returnReview",
      event: "change",
      value: { policyChecked: event },
      subComponent: "policyCheckbox",
    });
  };

  const handleEmailChange = (event: string) => {
    onChange?.({
      area: "returnReview",
      event: "change",
      value: { mailTo: event },
      subComponent: "mailToInput",
    });
  };

  const handleSavePreferencesChange = (event: boolean) => {
    onChange?.({
      area: "returnReview",
      event: "change",
      value: { savePreferences: event },
      subComponent: "savePreferences",
    });
  };

  const handleReshopOptionsChange = (event: ReshopOptions) => {
    onChange?.({
      area: "returnReview",
      event: "change",
      value: { reshopOptions: event },
      subComponent: "reshopOptions",
    });
  };

  const handleOnFooterShow = (event: StickyFooterOnShowEvent) => {
    setErrorPosition(
      event.inView && event.display === "inline" ? "footer" : "inline",
    );
  };

  const isEmailFieldStateValid = checkIfEmailFieldIsValid(mailTo, {
    canAddCustomerEmail,
    canUpdateCustomerEmailHipaa,
    hasAssociatedEmail,
  });

  const submitEnabled = [
    isEmailFieldStateValid,
    policyLabel ? policyChecked : true,
    !loading,
    !submitting,
    !error || allowRetry,
    !disabled,
  ].every(Boolean);

  return (
    <>
      <div
        className={styles.reviewSummaryContainer}
        style={bodytextDefaultNone}>
        <ReviewSummaryContent
          {...{
            canAddCustomerEmail,
            canUpdateCustomerEmailHipaa,
            locale,
            includeCurrency,
            orderItems,
            selectedItems,
            shopNowItems,
            hasClaim,
            shopName,
            isCertified,
            onChangeCertified,
            signature,
            onChangeSignature,
            fees,
            refund,
            dropOffLocation,
            error: errorPosition === "inline" ? error : null,
            hasAssociatedEmail,
            loading,
            mailTo,
            renderAddOn,
            returnCreditsSummary,
            onEmailChange: handleEmailChange,
            policyChecked,
            policyLabel,
            onPolicyChange: handlePolicyChange,
            preferencesStatus,
            savePreferences,
            onSavePreferencesChange: handleSavePreferencesChange,
            userRole,
            reshopOptions,
            onReshopOptionsChange: handleReshopOptionsChange,
          }}
        />
      </div>
      {!loading && !submitting && returnCart?.pudoOption && (
        <StickyFooter onShow={handleOnFooterShow}>
          <Footer
            progressiveStep={progressiveStep}
            activeStep={activeStep}
            onEvent={handleFooterEvent}
            buttons={{
              leftButton: {
                type: "back",
                label: backLabel,
                outline: true,
              },
              rightButton: {
                type: "submit",
                label: submitLabel,
                disabled: !submitEnabled,
              },
            }}
            below={
              error &&
              errorPosition === "footer" && (
                <div style={{ margin: "1rem" }}>
                  <ErrorBanner>
                    {React.isValidElement(error) ? (
                      error
                    ) : (
                      <Language langKey="returnsReviewError" />
                    )}
                  </ErrorBanner>
                </div>
              )
            }
          />
        </StickyFooter>
      )}
      {submitting && (
        <Backdrop>
          <Spinner width={50} />
          <SemibigHeader>
            <Language
              langKey={
                hasClaim
                  ? "claimsReviewSubmittingClaim"
                  : "returnsReviewSubmittingReturn"
              }
            />
          </SemibigHeader>
        </Backdrop>
      )}
    </>
  );
};

export const ReviewSummaryRaw = withSettingsProvider(
  withTranslationsProvider(
    withThemeProvider(ReviewSummaryRawC, {
      themeTokens: [
        "bodytextDefaultNone",
        "panelcontainerNoneNone",
        "panelcontainerNoneActive",
        "panelcontainerNoneHover",
      ],
    }),
    { translationKeys: ["returnsActionsBack", "returnsActionsSubmit"] },
  ),
  {
    settingsRequested: `
      itemDisplay {
        additionalItemAttributeConfig {
          attribute
          value {
            concatKey
            concatPosition
            label
            transform
          }
        }
        itemAttributeConfig
      }
    `,
  },
);
